// front/packages/design-system/src/ui/a/AreaChart/AreaChart.jsx

import * as React from 'react';
import PropTypes from 'prop-types';
import { AreaChart as RechartsAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import styleNames from '@pareto-engineering/bem/exports';
import "./styles.scss";

// Local Definitions

const baseClassName = styleNames.base;
const componentClassName = 'area-chart';
const AreaChart = ({
  id,
  className: userClassName,
  data,
  title,
  xKey,
  yKeys,
  xLabel,
  yLabel,
  colors,
  filled,
  height,
  width
  // ...otherProps
}) => {
  const processedData = data.map(item => {
    const yValues = yKeys.map(key => item[key]);
    const lowerBound = Math.min(...yValues);
    const upperBound = Math.max(...yValues);
    const margin = (upperBound - lowerBound) * 0.1;
    return {
      ...item,
      bounds: [lowerBound - margin, upperBound + margin]
    };
  });
  const yAxisBounds = () => {
    const yValues = data.map(item => yKeys.map(key => item[key]));
    const min = Math.min(...yValues.flat());
    const max = Math.max(...yValues.flat());
    const margin = (max - min) * 0.1;
    return [min - margin, max + margin];
  };
  const CustomTooltipContent = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      const newPayload = payload.filter(item => item.name !== 'bounds');
      return /*#__PURE__*/React.createElement("div", {
        className: "custom-tooltip"
      }, /*#__PURE__*/React.createElement("p", {
        className: "label"
      }, `${xLabel}: ${label}`), newPayload.map(entry => /*#__PURE__*/React.createElement("p", {
        className: "label",
        key: `${entry.name}`,
        style: {
          color: entry.color
        }
      }, `${entry.name}: ${entry.value}`)));
    }
    return null;
  };
  const CustomLegend = ({
    colorsArray,
    yKeysArray
  }) => /*#__PURE__*/React.createElement("div", {
    className: "custom-legend"
  }, yKeysArray.map((key, index) => /*#__PURE__*/React.createElement("div", {
    key: key,
    className: "item"
  }, /*#__PURE__*/React.createElement("span", {
    className: "line",
    style: {
      backgroundColor: colorsArray[index]
    }
  }), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, key))));
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: [baseClassName, componentClassName, userClassName].filter(e => e).join(' ')
  }, /*#__PURE__*/React.createElement("h3", null, title), /*#__PURE__*/React.createElement(CustomLegend, {
    colorsArray: colors,
    yKeysArray: yKeys
  }), /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: width,
    height: height
  }, /*#__PURE__*/React.createElement(RechartsAreaChart, {
    data: processedData
  }, /*#__PURE__*/React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /*#__PURE__*/React.createElement(XAxis, {
    dataKey: xKey,
    label: {
      value: xLabel,
      position: 'insideBottom',
      offset: -5
    },
    axisLine: false,
    tickLine: false,
    tickCount: 3
  }), /*#__PURE__*/React.createElement(YAxis, {
    domain: yAxisBounds,
    scale: "linear",
    interval: "equidistantPreserveStart",
    label: {
      value: yLabel,
      angle: -90,
      position: 'left',
      offset: 1,
      style: {
        textAnchor: 'middle'
      }
    },
    padding: {
      top: 10,
      bottom: 10
    },
    axisLine: false,
    tickLine: true,
    tickFormatter: value => value.toFixed(2)
  }), /*#__PURE__*/React.createElement(Tooltip, {
    content: /*#__PURE__*/React.createElement(CustomTooltipContent, null)
  }), filled && /*#__PURE__*/React.createElement(Area, {
    id: "bounds",
    type: "linear",
    dataKey: "bounds",
    stroke: "none",
    fill: "var(--hard-ui-main-2)",
    fillOpacity: 0.4,
    activeDot: false,
    dot: false,
    label: false,
    isAnimationActive: false
  }), yKeys.map((key, index) => /*#__PURE__*/React.createElement(Area, {
    id: key,
    key: key,
    type: "linear",
    dataKey: key,
    stroke: colors[index],
    fill: "none",
    connectNulls: true,
    dot: false,
    activeDot: {
      r: 4
    },
    isAnimationActive: false
  })))));
};
AreaChart.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  xKey: PropTypes.string.isRequired,
  yKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  filled: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
AreaChart.defaultProps = {
  filled: false,
  width: '100%',
  height: 300
};
export default AreaChart;